import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'edpad_install_prompt_dismissed_v1'

/**
 * PWA install prompt — показывается на authenticated страницах
 * (не на лендинге) если браузер поддерживает PWA install и пользователь
 * ещё не отказался / не установил.
 */
export function InstallPrompt() {
  const { t } = useTranslation()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // Показываем не сразу — даём пользователю освоиться
      setTimeout(() => setVisible(true), 3000)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (!visible || !deferredPrompt) return null

  const handleInstall = async () => {
    await deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    if (choice.outcome === 'dismissed') {
      localStorage.setItem(DISMISSED_KEY, '1')
    }
    setDeferredPrompt(null)
    setVisible(false)
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1')
    setVisible(false)
  }

  return (
    <div className="fixed bottom-3 left-3 right-3 md:left-auto md:right-4 md:bottom-4 md:max-w-sm z-40 bg-white border border-purple-100 rounded-2xl shadow-2xl p-4 animate-fade-up">
      <div className="flex items-start gap-3">
        <img src="/pwa-192.png" alt="EdPad" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 mb-1">{t('installPrompt.title')}</h3>
          <p className="text-xs text-gray-600 leading-relaxed">
            {t('installPrompt.description')}
          </p>
        </div>
      </div>
      <div className="flex gap-2 mt-3 justify-end">
        <button onClick={handleDismiss} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2">
          {t('installPrompt.dismiss')}
        </button>
        <button onClick={handleInstall} className="btn-primary text-xs">
          {t('installPrompt.install')}
        </button>
      </div>
    </div>
  )
}
